import {
  createContext,
  use,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import * as analytics from '../../shared/analytics/analytics.json';
import { useAnalytics } from '../../shared/analytics/create-events';
import { ModuleList } from '../../shared/analytics/module-list';
import { CommandDashboardTileDefinition } from './tile-config';
import { tileRefreshDisabledReason } from './use-tile-access';

export interface TileRefreshRegistration {
  onRefresh: () => void;
  isRefreshing?: boolean;
  refreshDisabledReason?: string;
}

export const TileRefreshContext = createContext<
  ((value: TileRefreshRegistration | null) => void) | null
>(null);

/**
 * Lets a tile body publish Refresh to the shell's kebab menu. The grid always
 * mounts the shell; bodies do not wrap CommandDashboardTile themselves.
 *
 * onRefresh is read through a ref so an inline handler in the body cannot
 * retrigger this effect (and the shell's setState) on every render.
 *
 * No-ops when the body is rendered outside a shell (isolated body tests).
 */
export function useTileRefresh({
  onRefresh,
  isRefreshing = false,
  refreshDisabledReason,
}: TileRefreshRegistration) {
  const setRefresh = use(TileRefreshContext);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useLayoutEffect(() => {
    if (!setRefresh) {
      return undefined;
    }
    setRefresh({
      onRefresh: () => onRefreshRef.current(),
      isRefreshing,
      refreshDisabledReason,
    });
    return () => setRefresh(null);
  }, [setRefresh, isRefreshing, refreshDisabledReason]);
}

export function useTrackCommandDashboardTileRefresh() {
  const { createEvent } = useAnalytics();
  return useCallback(() => {
    createEvent(analytics.command_dashboard.tile_management.refresh_tile, {
      module: ModuleList.modules.commandDashboard.name,
      section: ModuleList.modules.commandDashboard.sections.tileManagement,
    });
  }, [createEvent]);
}

/**
 * Registers Refresh on the shell, including the missing-permission tooltip.
 * `hasPermission` comes from the body (so the data hook can be disabled first).
 */
export function useBindTileRefresh(
  tile: CommandDashboardTileDefinition,
  hasPermission: boolean,
  {
    onRefresh,
    isFetching,
  }: {
    onRefresh: () => void | Promise<void>;
    isFetching: boolean;
  }
) {
  useTileRefresh({
    onRefresh,
    isRefreshing: isFetching,
    refreshDisabledReason: tileRefreshDisabledReason(tile, hasPermission),
  });
}

/**
 * Query-backed tiles: refetch, fire refresh_tile analytics on success, and
 * register Refresh on the shell.
 */
export function useBindQueryTileRefresh(
  tile: CommandDashboardTileDefinition,
  hasPermission: boolean,
  query: Pick<UseQueryResult, 'refetch' | 'isFetching'>
) {
  const trackRefresh = useTrackCommandDashboardTileRefresh();
  const onRefresh = async () => {
    const result = await query.refetch();
    if (!result.isError) {
      trackRefresh();
    }
  };
  useBindTileRefresh(tile, hasPermission, {
    onRefresh,
    isFetching: query.isFetching,
  });
}
